import React from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'rebass';
import { BiPlus, BiPlusCircle, BiUpArrowCircle, BiX } from 'react-icons/bi';
import {
  UnassignedCard,
  CardTitle,
  SpecText,
  EntitlementChip,
  RemoveButton,
  PrimaryButton,
  OutlineButton,
  AddOnsLink
} from '../styled';
import { entitlementLabel, specLine } from '../entitlements';
import Chevron from '../chevron';

// An unassigned journal (GET /v3/child_journals?section=unassigned) shown in the
// Unassigned tab next to the unassigned book entitlements.
//
// It uses the same card, chips and CTAs as UnassignedBookTile — the difference is
// that the journal already exists, so "Set up" goes straight into the set-up flow
// with its id rather than creating one first.
const UnassignedJournalTile = ({
  journal,
  assignedChips,
  pendingChips,
  isAddOnsMode,
  hasSelection,
  disabled,
  onSetUp,
  onAddToBook,
  onRemovePending,
  bookNumber,
  onUpgradeBundle,
  onPurchase
}) => {
  const spec = specLine(journal.entitlements);

  return (
    <UnassignedCard>
      <CardTitle>{journal.title}</CardTitle>

      {!!spec && <SpecText>{spec}</SpecText>}

      {assignedChips.map(chip => (
        <EntitlementChip key={chip.id}>{entitlementLabel(chip)}</EntitlementChip>
      ))}

      {pendingChips.map(chip => (
        <EntitlementChip key={chip.id} pending>
          {entitlementLabel(chip)}
          <RemoveButton type="button" onClick={() => onRemovePending(chip.id)}>
            Remove
            <BiX size={20} />
          </RemoveButton>
        </EntitlementChip>
      ))}

      {isAddOnsMode ? (
        <OutlineButton
          type="button"
          disabled={!hasSelection}
          onClick={() => onAddToBook(journal.id)}
        >
          <BiPlus size={20} />
          Add to this book
        </OutlineButton>
      ) : (
        <PrimaryButton type="button" disabled={disabled} onClick={() => onSetUp(journal.id)}>
          Set up
          <Chevron />
        </PrimaryButton>
      )}
      <Flex>
        {journal.entitlements?.['plan-name'] !== 'Platinum Bundle' && <AddOnsLink type="button" onClick={() => onUpgradeBundle(journal, false)}>
          <BiUpArrowCircle size={18} />
          Upgrade Bundle
        </AddOnsLink>}
        {journal.entitlements?.['plan-name'] !== 'Platinum Bundle' && <Flex width="1px" bg="#D1D1D1" height="44px" />}
        <AddOnsLink type="button" onClick={() => onPurchase(journal, false)}>
          <BiPlusCircle size={18} />
          Add-ons
        </AddOnsLink>
      </Flex>
    </UnassignedCard>
  );
};

UnassignedJournalTile.propTypes = {
  journal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    entitlements: PropTypes.object,
  }).isRequired,
  assignedChips: PropTypes.array,
  pendingChips: PropTypes.array,
  isAddOnsMode: PropTypes.bool,
  hasSelection: PropTypes.bool,
  disabled: PropTypes.bool,
  onSetUp: PropTypes.func.isRequired,
  onAddToBook: PropTypes.func.isRequired,
  onRemovePending: PropTypes.func.isRequired,
};

UnassignedJournalTile.defaultProps = {
  assignedChips: [],
  pendingChips: [],
  isAddOnsMode: false,
  hasSelection: false,
  disabled: false,
};

export default UnassignedJournalTile;
